import { useSidesFilter } from '@src/hooks/filters/useSidesFilter';
import { useSubAccountsFilter } from '@src/hooks/filters/useSubAccountsFilter';
import { isExcludedByResolvedUsers, useResolvedUsers, useUsersFilter } from '@src/hooks/filters/useUsersFilter';
import { useUser } from '@src/hooks/useUser';
import type { OTCDealData } from '@src/types/OTC';
import { isOptionDeal } from '@src/types/OTC/otcGuards';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  DealStatusEnum,
  EMPTY_OBJECT,
  FilterClauseType,
  IconName,
  OTCSettlementTypeEnum,
  ProductTypeEnum,
  StrikeTypeEnum,
  SubProductTypeEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  isOTCForwardSecurity,
  isOTCOptionSecurity,
  removeEmptyFilters,
  useDateRangeFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type Security,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { isEqual, keys, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';

const EMPTY_SECURITIES_BY_SYMBOL = new Map<string, Security>();

export interface BlotterTableOTCDerivativesFilter extends DateRangeFilter {
  Markets?: string[];
  Sides?: string[];
  Instruments?: string[];
  ProductTypes?: ProductTypeEnum[];
  SubProductTypes?: SubProductTypeEnum[];
  Counterparties?: string[];
  CounterpartyTypes?: string[];
  /** These are the deal status texts we are filtering by on the client side */
  _statuses?: DealStatusEnum[];
  /** These are the deal statuses that we are filtering by on the server */
  Statuses?: DealStatusEnum[];
  Users?: string[];
  SubAccounts?: string[];
  CSA?: string[];
}

interface UseOTCDerivativesFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
  securitiesBySymbol?: Map<string, Security>;
  enableOTCDeltaStrikeSupport?: boolean;
  /** When false, rows whose product is explicitly Physical-settled are hidden (resolved via security). */
  enableOTCPhysicalSettlement?: boolean;
}

export type UseOTCDerivativesFilterOutput<TData extends OTCDealData> = {
  initialFilter: BlotterTableOTCDerivativesFilter | undefined;
  filter: BlotterTableOTCDerivativesFilter;
  clientSideFilter: BlotterTableClientLocalFilter<TData>;
  changeFilter: (action: SetStateAction<BlotterTableFilter>) => void;
  filterableProperties: FilterableProperty[];
  filterBuilderProps: UseFilterBuilderProps;
  blotterTableFilterProps: Partial<BlotterTableFiltersProps>;
};

export function useOTCDerivativesFilter<TData extends OTCDealData>({
  persistedBlotterTable,
  securitiesBySymbol = EMPTY_SECURITIES_BY_SYMBOL,
  enableOTCDeltaStrikeSupport = true,
  enableOTCPhysicalSettlement = true,
}: UseOTCDerivativesFilterParams): UseOTCDerivativesFilterOutput<TData> {
  const [filter, setFilter] = useState(() => ({
    ...cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter),
  }));
  const { Name: currentUserName } = useUser();

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const prevFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;
      if (!isEqual(prevFilter, newFilter)) {
        setFilter(newFilter);
        persistedBlotterTable.onFilterChanged(newFilter);
      }
    },
    [filter, persistedBlotterTable]
  );

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<TData>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }

      // All other filtering is done client-side (except Statuses which is server-side)
      if (filterExistsAndExcludes(filter, 'Markets', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Sides', data, 'DealSide')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Instruments', data, 'Symbol')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Counterparties', data, 'Counterparty')) {
        return false;
      }
      // Statuses filtering is done server-side only (like Orders)
      if (isExcludedByResolvedUsers(resolvedUsers, getOtcDealDisplayUser(data))) {
        return false;
      }
      if (filter.SubAccounts && filter.SubAccounts.length > 0) {
        if (!data.SubAccount || !filter.SubAccounts.includes(data.SubAccount)) {
          return false;
        }
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'Status')) {
        return false;
      }

      if (isOptionDeal(data)) {
        const security = data.Symbol ? securitiesBySymbol.get(data.Symbol) : undefined;
        if (
          security &&
          isOTCOptionSecurity(security) &&
          security.ProductDetails.StrikeType === StrikeTypeEnum.Delta &&
          !enableOTCDeltaStrikeSupport
        ) {
          return false;
        }
      }

      if (!enableOTCPhysicalSettlement) {
        const security = data.Symbol ? securitiesBySymbol.get(data.Symbol) : undefined;
        if (security && (isOTCForwardSecurity(security) || isOTCOptionSecurity(security))) {
          if (security.ProductDetails.SettlementType === OTCSettlementTypeEnum.Physical) {
            return false;
          }
        }
      }
      return true;
    },
    [filter, resolvedUsers, securitiesBySymbol, enableOTCDeltaStrikeSupport, enableOTCPhysicalSettlement]
  );

  const sidesFilter = useSidesFilter();
  const usersFilter = useUsersFilter();
  const subAccountsFilter = useSubAccountsFilter({});

  const filterableProperties: FilterableProperty[] = useMemo(
    () =>
      [
        sidesFilter,
        {
          key: 'Statuses',
          label: 'Status',
          icon: IconName.CheckCircle,
          options: values(DealStatusEnum),
          getOptionLabel: (option: string) => option,
        },
        subAccountsFilter,
        usersFilter,
        {
          key: 'CSA',
          label: 'CSA',
          icon: IconName.Document,
          options: [],
          getOptionLabel: (option: string) => option,
        },
      ].filter(Boolean),
    [sidesFilter, subAccountsFilter, usersFilter]
  );

  const propertiesByKey = useMemo(() => {
    return new Map(filterableProperties.map(prop => [prop.key, prop]));
  }, [filterableProperties]);

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      keys(filter).forEach(key => {
        if (key === '_start' || key === 'StartDate' || key === 'TimestampField') {
          return;
        }
        const value = filter[key as keyof BlotterTableOTCDerivativesFilter];
        if (!value) {
          return;
        }
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: Array.isArray(value) ? value : [value],
        });
      });
    }
    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter: BlotterTableOTCDerivativesFilter = removeEmptyFilters<BlotterTableOTCDerivativesFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => {
              const selections = filterClausesByPropertyKey.get(key)?.selections;
              return [key, selections];
            })
          ) satisfies BlotterTableOTCDerivativesFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, propertiesByKey]
  );

  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;

  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;

  return {
    initialFilter: persistedBlotterTable.initialState?.filter,
    filter,
    clientSideFilter,
    changeFilter,
    filterableProperties,
    filterBuilderProps,
    blotterTableFilterProps,
  };
}

// Helper function for column ID to filter builder key mapping
const colIDToFilterBuilderKeyMap = {
  Symbol: 'Instruments',
  DealSide: 'Sides',
  Counterparty: 'Counterparties',
  CounterpartyRole: 'CounterpartyTypes',
  Status: 'Statuses',
  user: 'Users',
  SubAccount: 'SubAccounts',
} as const;

export function colIDToFilterBuilderKey(id: string): keyof BlotterTableOTCDerivativesFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}

// Helper function to filter only server-side filter keys
export function onlyServerFilterKeys(filter?: BlotterTableOTCDerivativesFilter) {
  if (!filter) {
    return EMPTY_OBJECT;
  }

  const serverFilter: BlotterTableOTCDerivativesFilter = {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
    Markets: filter.Markets,
    Sides: filter.Sides,
    Instruments: filter.Instruments,
    ProductTypes: [ProductTypeEnum.OTC], // Always filter for OTC products
    SubProductTypes: [SubProductTypeEnum.OTCForward, SubProductTypeEnum.OTCVanillaOption], // Always filter for OTC products
    Counterparties: filter.Counterparties,
    CounterpartyTypes: filter.CounterpartyTypes,
    Users: filter.Users,
    SubAccounts: filter.SubAccounts,
    CSA: filter.CSA,
  };

  // Convert _statuses to Statuses for server-side filtering (same pattern as Orders)
  if (filter._statuses) {
    serverFilter.Statuses = filter._statuses;
  } else if (filter.Statuses) {
    serverFilter.Statuses = filter.Statuses;
  }

  return serverFilter;
}
